'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

const PLANS = [
  {
    name: 'Free',
    price: '$0',
    period: '/month',
    description: 'Get started with essential career tools',
    cta: 'Get Started Free',
    href: '/register',
    highlight: false,
    features: [
      'Basic Resume Builder',
      'Basic resume templates',
      'Limited PDF exports',
      'Limited AI resume suggestions',
      'Basic Scholarship Finder',
      'Basic Career Search',
      'Limited Video Templates',
      'Basic resume content analysis',
    ],
  },
  {
    name: 'Premium',
    price: '$9.99',
    period: '/month',
    description: 'Advanced tools for serious job seekers',
    cta: 'Upgrade to Premium',
    href: '/pricing',
    highlight: true,
    badge: 'Most Popular',
    features: [
      'Everything in Free',
      'More AI requests',
      'Advanced Resume Builder',
      'Job Description Analyzer',
      'AI Resume Tailoring',
      'More PDF exports',
      'More resume versions',
      'Expanded Scholarship Finder',
      'Saved scholarships',
      'ATS keyword analysis',
    ],
  },
  {
    name: 'Max',
    price: '$15.99',
    period: '/month',
    description: 'The complete career acceleration platform',
    cta: 'Upgrade to Max',
    href: '/pricing',
    highlight: false,
    badge: 'All Features',
    features: [
      'Everything in Premium',
      'Apply Smarter (1-click kit)',
      'Advanced AI Resume Tailoring',
      'Resume Intelligence Dashboard',
      'AI Interview Coach',
      'Career Match',
      'Scholarship Match Pro',
      'Unlimited Resume Versions',
      'Advanced Career Content Studio',
      'Priority AI usage',
    ],
  },
];

function PlanCard({ plan, delay }: { plan: typeof PLANS[0]; delay: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`relative rounded-2xl flex flex-col transition-all duration-300 ${
        plan.highlight
          ? 'bg-blue-600 text-white shadow-2xl shadow-blue-200 scale-105 border-2 border-blue-500'
          : 'bg-white border border-blue-100 shadow-sm hover:shadow-md hover:border-blue-300'
      }`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? (plan.highlight ? 'scale(1.05) translateY(0)' : 'translateY(0)') : 'translateY(24px)',
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
      }}
    >
      {plan.badge && (
        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold ${
          plan.highlight ? 'bg-white text-blue-600' : 'bg-blue-600 text-white'
        }`}>
          {plan.badge}
        </div>
      )}

      <div className="p-6 flex-1">
        <div className="mb-4">
          <h3 className={`text-lg font-bold ${plan.highlight ? 'text-white' : 'text-slate-800'}`}>{plan.name}</h3>
          <p className={`text-xs mt-1 ${plan.highlight ? 'text-blue-100' : 'text-slate-500'}`}>{plan.description}</p>
        </div>

        <div className="flex items-baseline gap-1 mb-6">
          <span className={`text-4xl font-bold ${plan.highlight ? 'text-white' : 'text-slate-800'}`}>{plan.price}</span>
          <span className={`text-sm ${plan.highlight ? 'text-blue-200' : 'text-slate-400'}`}>{plan.period}</span>
        </div>

        <ul className="space-y-2.5">
          {plan.features.map(feature => (
            <li key={feature} className="flex items-start gap-2.5 text-sm">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                className={`shrink-0 mt-0.5 ${plan.highlight ? 'text-blue-200' : 'text-blue-500'}`}>
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span className={plan.highlight ? 'text-blue-50' : 'text-slate-600'}>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="p-6 pt-0">
        <Link
          href={plan.href}
          className={`w-full flex items-center justify-center py-3 rounded-xl font-bold text-sm transition-all ${
            plan.highlight
              ? 'bg-white text-blue-600 hover:bg-blue-50' :'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {plan.cta}
        </Link>
      </div>
    </div>
  );
}

export default function PlansSection() {
  return (
    <section className="py-20 px-4 sm:px-6 bg-gradient-to-b from-blue-50/50 to-white" id="pricing">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-4 bg-blue-50 text-blue-600 border border-blue-200">
            Simple Pricing
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800">
            Start free.{' '}
            <span className="text-blue-600">Upgrade when you&apos;re ready.</span>
          </h2>
          <p className="mt-3 text-slate-500 max-w-lg mx-auto text-sm sm:text-base">
            No credit card required to start. Upgrade to unlock advanced AI tools and premium features.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 items-center max-w-5xl mx-auto">
          {PLANS.map((plan, i) => <PlanCard key={plan.name} plan={plan} delay={i * 100} />)}
        </div>

        <p className="text-center text-xs text-slate-400 mt-8">
          No credit card required for the Free plan. Cancel anytime. Prices in USD.
        </p>
      </div>
    </section>
  );
}
